/** User-facing copy uses `@handle`; API/storage values omit the prefix. */

import { USERNAME_DISPLAY_MAX_LENGTH } from '../constants/common';

export const USERNAME_AT_PREFIX = '@';

const USERNAME_PAYLOAD_KEYS = [
  'username',
  'creator_username',
  'creatorUsername',
  'from_username',
  'fromUsername',
  'senderUsername',
  'senderDisplay',
];

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Strip a leading `@` and trim; returns empty string for invalid input. */
export function stripUsernameAt(value) {
  if (value == null) return '';
  const text = String(value).trim();
  if (!text) return '';
  return text.startsWith(USERNAME_AT_PREFIX) ? text.slice(1) : text;
}

/** Display handle with exactly one leading `@`. */
export function formatUsernameDisplay(value) {
  const bare = stripUsernameAt(value);
  if (!bare) return '';
  return `${USERNAME_AT_PREFIX}${bare}`;
}

/** Compact `@handle` for cards and lists; truncates after `USERNAME_DISPLAY_MAX_LENGTH`. */
export function truncateUsernameDisplay(
  value,
  maxLength = USERNAME_DISPLAY_MAX_LENGTH
) {
  const display = formatUsernameDisplay(value);
  if (!display || display.length <= maxLength) return display;
  return `${display.slice(0, maxLength)}...`;
}

/** Read the primary handle from FCM / activity notification payloads. */
export function readUsernameFromPayload(payload = {}) {
  if (!payload || typeof payload !== 'object') return '';

  for (const key of USERNAME_PAYLOAD_KEYS) {
    const candidate = payload[key];
    if (candidate == null || candidate === '') continue;
    const display = formatUsernameDisplay(candidate);
    if (display) return display;
  }

  return '';
}

/**
 * Replace bare `username` tokens with `@username` in notification copy.
 * Leaves text unchanged when `@username` is already present or username is missing.
 */
export function ensureAtMentionInText(text, username) {
  if (!text || typeof text !== 'string') return text || '';

  const display = formatUsernameDisplay(username);
  const bare = stripUsernameAt(username);
  if (!bare) return text;

  if (new RegExp(`@${escapeRegExp(bare)}\\b`, 'i').test(text)) {
    return text;
  }

  const barePattern = new RegExp(`\\b${escapeRegExp(bare)}\\b`, 'gi');
  return text.replace(barePattern, (match, offset, fullText) => {
    if (offset > 0 && fullText[offset - 1] === USERNAME_AT_PREFIX) {
      return match;
    }
    return display;
  });
}
